import { CaseInsensitiveStringMap, Nullable, NullableResultPromise, type Optional } from "../../base-types";
import { IContentHubClient } from "../../clients/content-hub-client";
import type CultureInfo from "../../culture-info";
import Guard from "../../guard";
import { MappingUtilities } from "../../mappers/mapping-utilities";
import { EntityLoadConfiguration } from "../querying/entity-load-configuration";
import { LoadOption } from "../querying/load-options";
import { IPropertyLoadOption, PropertyLoadOption } from "../querying/property-load-option";
import { RelationLoadOption } from "../querying/relation-load-option";
import { IEntity } from "./entity";
import { LazyLoadingManager } from "./lazy-loading-manager";
import { IProperty } from "./property";

export class PropertyManager {
    private readonly _client: IContentHubClient;
    private readonly _entity: IEntity;
    private readonly _properties: CaseInsensitiveStringMap<IProperty>;

    get count(): number {
        return Object.keys(this._properties).length;
    }

    constructor(client: IContentHubClient, properties: Array<IProperty>, entity: IEntity) {
        Guard.notNullOrUndefined(entity);
        Guard.notNullOrUndefined(client);
        Guard.notNullOrUndefined(properties);

        this._client = client;
        this._entity = entity;
        this._properties = new CaseInsensitiveStringMap<IProperty>();

        for (const property of properties) {
            this._properties[property.name] = property;
        }
    }

    getProperty<T extends IProperty = IProperty>(name: string): Nullable<T> {
        Guard.stringNotNullOrEmpty(name);

        return (this._properties[name] as T) || null;
    }

    getProperties(): Array<IProperty> {
        return [...Object.values(this._properties)];
    }

    async loadPropertyAsync<T extends IProperty = IProperty>(
        propertyName: string,
        culture: Optional<CultureInfo>
    ): NullableResultPromise<T> {
        Guard.stringNotNullOrEmpty(propertyName);
        LazyLoadingManager.ensureLazyLoadingIsPossible(this._entity);

        // Check if it is already loaded
        let property: Nullable<T> = this._properties[propertyName] as T;

        if (property) {
            return property;
        }

        // Load the property
        property = await this.fetchPropertyAsync<T>(propertyName, culture);

        // Add the property
        if (property != null) {
            this._properties[property.name] = property;
        }

        return property;
    }

    async loadPropertiesAsync(
        propertyLoadOption: IPropertyLoadOption,
        culture: Optional<CultureInfo>
    ): Promise<boolean> {
        Guard.notNullOrUndefined(propertyLoadOption);
        LazyLoadingManager.ensureLazyLoadingIsPossible(this._entity);

        if (!this.hasMissingProperties(propertyLoadOption)) {
            return false;
        }

        const cultures = new Set<CultureInfo>([...this._entity.cultures]);

        if (culture) {
            cultures.add(culture);
        }

        const cultureLoadOption = MappingUtilities.culturesToLoadOption([...cultures]);
        const loadConfig = new EntityLoadConfiguration(cultureLoadOption, propertyLoadOption, RelationLoadOption.None);

        const tempEntity = await this._client.entities.getAsync(this._entity.id!, loadConfig);

        this.importMissingProperties(tempEntity);

        return true;
    }

    hasMissingProperties(propertyLoadOption: IPropertyLoadOption): boolean {
        if (
            propertyLoadOption == null ||
            propertyLoadOption.loadOption === LoadOption.None ||
            propertyLoadOption.properties == null ||
            propertyLoadOption.properties.length === 0
        ) {
            return false;
        }

        if (propertyLoadOption.loadOption === LoadOption.All) {
            // We don't have any info about the definition here. (Same logic as in the C# SDK)
            return true;
        }

        return propertyLoadOption.properties.some(requestedPropertyName => !this._properties[requestedPropertyName]);
    }

    importMissingProperties(entity: Nullable<IEntity>): void {
        Guard.notNullOrUndefined(entity);

        for (const property of entity!.properties) {
            if (!this._properties[property.name]) {
                this._properties[property.name] = property;
            }
        }
    }

    private async fetchPropertyAsync<T extends IProperty = IProperty>(
        name: string,
        culture: Optional<CultureInfo>
    ): NullableResultPromise<T> {
        const cultures = new Set<CultureInfo>([...this._entity.cultures]);

        if (culture) {
            cultures.add(culture);
        }

        // Setup load configuration
        const cultureLoadOption = MappingUtilities.culturesToLoadOption([...cultures]);
        const propertyLoadOption = new PropertyLoadOption(name);
        const loadConfig = new EntityLoadConfiguration(cultureLoadOption, propertyLoadOption, RelationLoadOption.None);

        // Load entity
        const entity = await this._client.entities.getAsync(this._entity.id!, loadConfig);
        if (entity == null) {
            return null;
        }

        // We only loaded this specific property, so we can just take the first one.
        const property = entity.properties[0] || null;

        return property as T;
    }
}
